import { IConfig } from 'types';
import moment from 'moment';

export enum StorageKeys {
  CONFIG = 'CONFIG',
  REFERENCE_DATA = 'REFERENCE_DATA',
  USER_SETTING = 'USER_SETTING',
  SIDEBAR = 'showSidebar',
  OVERVIEW_COLLAPSE = 'overview_collapse',
  MS_RESULTS_HELP = 'msResultsHelp',
  LIFE_AD_RESULT = 'lifeAdResult',
  MBI_CTA_CLICKED = 'mbi_cta_clicked',
  MS_STEPPER_PURCHASED = 'ms_stepper_purchased',
  MARKET_SCAN_REFERENCE = 'marketScanReference',
}

export enum ReferenceDataKeys {
  POLICY_PROVIDERS = 'policyProviders',
  POLICY_SCAN = 'policyScan',
  POLICY_TYPES = 'policyTypes',
  WIKI = 'wiki',
}


export const getConfig = () => {
  const stringified = localStorage.getItem(StorageKeys.CONFIG);
  return stringified ? JSON.parse(stringified) : undefined;
};

export const setConfig = (config: IConfig) => {
  const stringified = JSON.stringify(config);
  localStorage.setItem(StorageKeys.CONFIG, stringified);
};

export const getReferenceData = (prop) => {
  const stringified = localStorage.getItem(
    `${StorageKeys.REFERENCE_DATA}_${prop}`,
  );
  return stringified ? JSON.parse(stringified) : {};
};

export const setReferenceData = (items, prop) => {
  const referenceWithDate = {
    items,
    lastUpdated: moment().format('YYYY-MM-DD'),
  };
  const stringified = JSON.stringify(referenceWithDate);
  localStorage.setItem(`${StorageKeys.REFERENCE_DATA}_${prop}`, stringified);
};

export const resolveReferenceData = async (config: IConfig, prop, asyncFn) => {
  const referenceLastUpdated = R.propOr(
    new Date(),
    'referenceLastUpdated',
    config,
  );
  const localReferenceData = getReferenceData(prop);
  // We fetch from API if data is not in local storage
  // or referenceLastUpdated is more recent than local lastUpdated
  if (
    !localReferenceData.items ||
    moment(referenceLastUpdated).isAfter(localReferenceData.lastUpdated)
  ) {
    const result = await asyncFn();
    const data = result.data.Items || result.data;
    setReferenceData(data, prop);
    return data;
  }
  return localReferenceData.items;
};

export const setUserSetting = (name, value) => {
  localStorage.setItem(`${StorageKeys.USER_SETTING}_${name}`, value);
};

export const getUserSetting = (name) => {
  return JSON.parse(
    localStorage.getItem(`${StorageKeys.USER_SETTING}_${name}`),
  );
};

// Persist the market-scan "reference" (the entry point a scan-creation flow
// began from — e.g. the Market Scan Exit Popup) for the whole journey. We use
// localStorage rather than Redux because the purchase flow finishes via
// external Windcave redirects that reload the page and wipe in-memory state;
// localStorage survives those so the purchase-success events still carry it.
export const setMarketScanReference = (reference?: string | null) => {
  try {
    if (reference) {
      localStorage.setItem(StorageKeys.MARKET_SCAN_REFERENCE, reference);
    } else {
      localStorage.removeItem(StorageKeys.MARKET_SCAN_REFERENCE);
    }
  } catch {
    // localStorage can throw in private mode / storage-disabled contexts.
  }
};

export const getMarketScanReference = (): string | undefined => {
  try {
    return localStorage.getItem(StorageKeys.MARKET_SCAN_REFERENCE) || undefined;
  } catch {
    return undefined;
  }
};

// Spreadable props for the market-scan tracking events — `{ reference }` when a
// reference is active, otherwise `{}` so the field is omitted entirely.
export const getMarketScanReferenceProps = (): { reference?: string } => {
  const reference = getMarketScanReference();
  return reference ? { reference } : {};
};

export const setSessionStorage = (name: string, value) => {
  sessionStorage.setItem(name, value);
};

export const getSessionStorage = (name) => {
  return JSON.parse(sessionStorage.getItem(name));
};

export const getSidebarState = () => {
  const value = getUserSetting(StorageKeys.SIDEBAR);
  if (value || value === false) {
    // Minimise sidebar on smaller screens by default
    return window.innerWidth > 992;
  }
  return getUserSetting(StorageKeys.SIDEBAR);
};

export const getPolicyTypeByName = (name: string) => {
  const { items = [] } = getReferenceData(ReferenceDataKeys.POLICY_TYPES);
  return items.find(R.propEq('name', name));
};

export const getPolicyProviderById = (id: string) => {
  const { items = [] } = getReferenceData(ReferenceDataKeys.POLICY_PROVIDERS);
  return items.find(R.propEq('id', id));
};

export const getPolicyProviderByName = (name: string) => {
  const { items = [] } = getReferenceData(ReferenceDataKeys.POLICY_PROVIDERS);
  return items.find((p) => p.name.toLowerCase() === name.toLowerCase());
};

export const getAreaByPostCode = (postCode: string) => {
  const { items } = getReferenceData(ReferenceDataKeys.POLICY_SCAN);
  const { areaPostCodes } = items;
  if (areaPostCodes) {
    return Object.keys(areaPostCodes).find((area: string) => {
      const postCodes = areaPostCodes[area];
      return R.includes(postCode, postCodes);
    });
  }
  return null;
};

export const isRiskZoneProperty = ({ postCode }) => {
  const { items } = getReferenceData(ReferenceDataKeys.POLICY_SCAN);
  const { riskZonePostCodes = [] } = items;
  return R.includes(postCode, riskZonePostCodes);
};

export const setDashOverviewCollapse = (value: boolean) => {
  setUserSetting(StorageKeys.OVERVIEW_COLLAPSE, value.toString());
};

export const getDashOverviewCollapse = () => {
  return getUserSetting(StorageKeys.OVERVIEW_COLLAPSE);
};

export const getWikiByProvider = (name: string) => {
  const { items = [] } = getReferenceData(ReferenceDataKeys.WIKI);
  return items.find(R.propEq('policyType', name));
};
